/**
 * game-start-toggles-etch.js — the ROWS group's frame-etch entrance gesture: the Multiplayer
 * bar and the control bank (the hints/learning toggles and the two card-preference pickers)
 * frame-etch as their own group in the start screen's entrance cascade (game-start-entrance.js
 * — LOGO / TILES / CARDS / ROWS, all overlapping from the entrance's own start, no closing
 * tail — the "hybrid" choreography, settled with Andre over four prototype rounds,
 * start-screen-entrance-animations, 2026-08-18). Each item's own frame draws on, then its
 * content blooms up under it. They graduated from a slide-up+fade once the rest of the screen
 * spoke etch: the slide read as a leftover dialect (SCREENS Decision 2). A light FRAME-ONLY
 * etch (the pill edge, not the knob or label) gives a third weight tier between the cards (full
 * etch) and the name/leaderboard (pure fade) — the screen stays one vocabulary without the
 * "etch everything reads busy" wash. Since the card-preference-pickers-redesign brief § 5, this
 * beat also covers the two `.segmented-picker` card-preference pickers alongside the toggles —
 * one shared group for the whole control bank rather than two dialects on one row
 * (`groupPillAndContent` below is the one place the three control shapes' "pill" and "content"
 * differ).
 *
 * Plays at natural tempo (real seconds), like every group in the cascade now — none of them
 * scaled to a fixed total (the old whole-introduction compression retired with the two-beat
 * story). Extracted from game-start-entrance.js (tbt-reshaping slice 1 §2, when the chunked
 * build pushed that module into the size-budget red band) — a self-contained build+shed etch
 * unit, mirroring game-start-panel-etch.js's shape. Consumers: game-start-entrance.js's
 * attachContentAndWireCompletion (addTogglesEtch, the build side — it weaves the returned
 * timeline onto the master and reads its duration for the leaderboard-gate re-key),
 * finishEntranceEtch (shedTogglesEtch, a Play click mid-inscription), and playGameStartExit
 * (EXIT_FADE_SELECTOR, fading the settled rows group out in one call — the strip tiles left this
 * selector for their own flight, below).
 *
 * SHED-SHAPE CONTRACT: animation/components/glass-condense.js's residue is the same
 * {svg, group, contents} shape as this module's (its fog pane rides the `svg` slot), so the
 * entrance merges the two groups' residues into one list and sheds it through shedTogglesEtch in
 * a single call — the tiles group, the menu button's pane, and the rows all at once. Stated on
 * both modules' headers deliberately: neither may change the shape without the other. The
 * component ships its own shedGlassCondense for every consumer outside this entrance.
 *
 * Since multiplayer-entry-and-notifications: the target set also includes the start-screen
 * Multiplayer section's bar (`.mp-section-bar`, game-start-multiplayer-section.js) — an
 * always-present structural element that joins this same group (design § "Entrance & motion"),
 * even though it sits outside `.game-start-control-bank`. It's its own pill (the bar IS the
 * pill — see groupPillAndContent's `.mp-section-bar` branch), unlike the toggle/picker shapes
 * above. The reference strip's six tiles (`.gs-strip-tile`) do NOT join this beat — they enter
 * via their own gesture instead, the entrance's TILES group (animation/components/
 * glass-condense.js, settled start-screen-entrance-animations 2026-08-18), and since
 * menu-strip-play-exit-flight they leave via their own gesture too
 * (game-start-strip-flight.js), so EXIT_FADE_SELECTOR no longer composes over them.
 */
import { gsap } from "../vendor/gsap.js";
import {
  framePath,
  FRAME_COLOR,
  FRAME_WIDTH,
} from "./game-start-panel-etch.js";

// The rows group's own membership: the control bank's toggle-groups and segmented-pickers,
// plus the Multiplayer bar — what this module's frame-etch builds, and what the exit's fade-out
// (EXIT_FADE_SELECTOR's sole consumer) sheds in one call. The strip tiles used to ride this
// selector for their exit fade; they fly instead now (game-start-strip-flight.js).
const ROWS_GROUP_SELECTOR =
  ".game-start-control-bank .toggle-group, .game-start-control-bank .segmented-picker, .mp-section-bar";
export const EXIT_FADE_SELECTOR = ROWS_GROUP_SELECTOR;
const TOGGLE_PILL_SELECTOR = ".hints-toggle";
const TOGGLE_LABEL_SELECTOR = ".hints-label";
const PICKER_PILL_SELECTOR = ".segmented-picker-options";

// The three rows-group shapes' "pill" (what the frame outlines) and "content" (what hides
// pre-bloom) differ: a toggle-group's pill is its switch, content is switch + label; a
// segmented-picker has no separate label anymore (label removal, brief § 2) — its pill (the
// options row) IS its whole content; the Multiplayer bar has no nested pill at all — it IS its
// own pill (the bar's `.mp-run` box), and its content is every one of its own children.
function groupPillAndContent(group) {
  if (group.classList.contains("segmented-picker")) {
    const pill = group.querySelector(PICKER_PILL_SELECTOR);
    return { pill, contents: [pill] };
  }
  if (group.classList.contains("mp-section-bar")) {
    // The bar itself (group === pill) must stay opaque + in flow, exactly like the toggle/
    // picker groups above — a hidden group can't paint the frame appended into it (the
    // invariant this function's header names). "Content" is the bar's own children (mark,
    // label, chevron), not the group, so `[group]` here was the actual bug: it hid the bar,
    // which hid the frame appended as its child too, so nothing painted until the bloom.
    return { pill: group, contents: [...group.children] };
  }
  const pill = group.querySelector(TOGGLE_PILL_SELECTOR);
  return {
    pill,
    contents: [pill, group.querySelector(TOGGLE_LABEL_SELECTOR)],
  };
}

// play-test: the rows group's tempo, in REAL seconds — like every group in the cascade, this
// isn't scaled. One pill outline's draw-on span, its bloom, and the per-item stagger — slow
// enough to read the stroke on a small 46×26 toggle pill; the stagger lets each item inscribe
// as its own distinct mark rather than one cluster. Originally tuned against two toggles; the
// brief § 5 extension carries the same numbers onto the picker pills too (ROWS_GROUP_SELECTOR
// above, up to four items under German) — untried at that count, so re-play-test if the group
// starts to feel long.
const TOGGLE_FRAME_DRAW_S = 0.5;
const TOGGLE_BLOOM_S = 0.32;
const TOGGLE_ETCH_STAGGER_S = 0.2;

/**
 * Compose the rows group's frame-etch onto a fresh timeline: inscribe each pill's outline (a
 * DrawSVG frame over the pill — the mode-card frame idiom, framePath, but frame-only), then
 * bloom the pill up under it, staggered. Plays at natural tempo (real seconds). Returns
 * { tl, residue }: the caller weaves `tl` onto its master at this group's own cascade position
 * (game-start-entrance.js's ROWS_GROUP_AT_S) and sheds `residue` — [{ svg, group, contents }] —
 * at the end. The same contract as game-start-panel-etch.js's addPanelEtch — and that contract is
 * what publishes this group's settle point: the last frame-fade IS the timeline's end, so the caller's
 * leaderboard-gate math reads `tl.duration()` rather than a mirrored formula.
 * (Reduced-motion/return never reach here.)
 */
export function addTogglesEtch(screen) {
  const tl = gsap.timeline();
  const groups = [...screen.querySelectorAll(ROWS_GROUP_SELECTOR)];
  const residue = groups.map((group, i) =>
    addOneToggleEtch(tl, group, i * TOGGLE_ETCH_STAGGER_S),
  );
  return { tl, residue };
}

// Inscribe one control at `at`: hide its CONTENT (groupPillAndContent above), draw the pill
// outline on, then bloom the content up under the stroke at the golden cut. The frame is a
// child of the group, positioned over the pill — but it's the content that's hidden,
// not the group, for two reasons that pull in opposite directions: a child of an
// `opacity:0` element can't paint (so the group can't be the hidden one), and a
// frame appended into the flex row lands after the group's last flex sibling, which then
// stops matching `:last-child` and regains margin it lost — shifting it (so the frame
// can't be a row sibling either). A frame inside a still-opaque, in-flow group threads
// both. Returns the shed handle.
function addOneToggleEtch(tl, group, at) {
  const { pill, contents } = groupPillAndContent(group);
  group.style.position = "relative"; // anchor the frame overlay (no row-layout effect)
  gsap.set(contents, { autoAlpha: 0 }); // hide the content — its bloom brings it up
  const svg = buildToggleFrameOverlay(group, pill);
  const path = svg.firstChild;
  gsap.set(path, { drawSVG: "0% 0%", opacity: 1 });
  tl.to(
    path,
    { drawSVG: "0% 100%", duration: TOGGLE_FRAME_DRAW_S, ease: "power1.inOut" },
    at,
  );
  // Bloom at the golden cut — the pill + knob + label fade up while the stroke still
  // finishes over them, so it reads "drawn then filled", not "fade + line".
  tl.to(
    contents,
    { autoAlpha: 1, duration: TOGGLE_BLOOM_S, ease: "power2.out" },
    at + TOGGLE_FRAME_DRAW_S * 0.618,
  );
  tl.to(
    path,
    { opacity: 0, duration: TOGGLE_BLOOM_S, ease: "power2.out" },
    at + TOGGLE_FRAME_DRAW_S,
  );
  return { svg, group, contents };
}

// An SVG frame sized to the pill's box, overlaid inside the (relative) toggle group
// over the pill — the pill's offsets resolve against the group, its offsetParent.
// Mirrors buildFrameOverlay in panel-etch, scaled to the small pill; appended to the
// group, not the flex row, so it never becomes the row's `:last-child` and shifts the
// last toggle (which would stop matching `:last-child` and regain the margin it lost).
//
// The Multiplayer bar is the shape where pill === group (groupPillAndContent's
// `.mp-section-bar` branch): `pill.offsetLeft`/`offsetTop` would then resolve against
// group's OWN offsetParent (some ancestor further up the tree), not against group itself —
// the svg is appended into group, so it needs 0/0 in that case to cover group's own border
// box exactly.
function buildToggleFrameOverlay(group, pill) {
  const w = pill.offsetWidth;
  const h = pill.offsetHeight;
  const left = pill === group ? 0 : pill.offsetLeft;
  const top = pill === group ? 0 : pill.offsetTop;
  const r = parseFloat(getComputedStyle(pill).borderTopLeftRadius) || 0;
  const svgNs = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(svgNs, "svg");
  svg.classList.add("game-start-toggle-frame"); // ephemeral animation DOM, shed at the end
  svg.setAttribute("viewBox", `0 0 ${w} ${h}`);
  svg.style.cssText =
    `position:absolute; left:${left}px; top:${top}px; ` +
    `width:${w}px; height:${h}px; overflow:visible; pointer-events:none;`;
  const path = document.createElementNS(svgNs, "path");
  path.setAttribute("d", framePath(w, h, r, FRAME_WIDTH / 2, "continuous"));
  path.setAttribute("fill", "none");
  path.setAttribute("stroke", FRAME_COLOR);
  path.setAttribute("stroke-width", FRAME_WIDTH);
  path.setAttribute("stroke-linecap", "round");
  path.setAttribute("stroke-linejoin", "round");
  svg.appendChild(path);
  group.appendChild(svg);
  return svg;
}

// Hand the etched toggles back to the stylesheet: drop the frame overlays, clear the
// GSAP residue so each toggle rests as the factory render, and un-anchor the group.
export function shedTogglesEtch(residue) {
  for (const { svg, group, contents } of residue) {
    svg.remove();
    gsap.set(contents, { clearProps: "all" });
    group.style.position = "";
  }
}
